import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Box, CardContent, CardHeader, Rating, Skeleton } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SummaryContent from './SummaryContent';
import { useSpaData } from '../../../../hooks';
import HideSummaryOrNoSummaryText from './HideSummaryOrNoSummaryText';

const classes = {
	summary: {
		padding: '24px',
		backgroundColor: (theme) => theme.palette.background.paper,
		marginY: '16px',
	},
};

const Summary = ({ feedbacksSummary, isSummaryPending, noData }) => {
	const { t } = useTranslation(['candidate-feedback']);
	const { hideNoRatingsAndRecommendations } = useSpaData();
	const { recommendations, averageRating, noSummaryText, hideSummaryUntilSubmitted } = useMemo(
		() => ({
			recommendations: t('recommendations'),
			averageRating: t('average_rating'),
			noSummaryText: t('no_summary'),
			hideSummaryUntilSubmitted: t(
				'feedback_summary_will_be_available_after_you_submit_your_feedback',
			),
		}),
		[t],
	);

	if (isSummaryPending) {
		return (
			<Box sx={classes.summary} data-testid="summary-skeleton">
				<CardHeader title={<Skeleton />} />
				<CardContent sx={classes.feedbackCardContentRoot}>
					<Skeleton />
				</CardContent>
				<CardHeader title={<Skeleton />} />
				<CardContent sx={classes.feedbackCardContentRoot}>
					<Skeleton />
				</CardContent>
			</Box>
		);
	}
	if (noData && !feedbacksSummary?.hideSummary && hideNoRatingsAndRecommendations) {
		return null;
	}

	return (
		<Box sx={classes.summary}>
			{noData ? (
				<>
					<HideSummaryOrNoSummaryText
						hideSummaryUntilSubmitted={hideSummaryUntilSubmitted}
						noSummaryText={noSummaryText}
						feedbacksSummary={feedbacksSummary}
						hideNoRatings={hideNoRatingsAndRecommendations}
					/>
				</>
			) : (
				<>
					{feedbacksSummary.recommendations.length > 0 && (
						<SummaryContent
							headerTitle={recommendations}
							shortSummaryTitle={feedbacksSummary.recommendationStatus.text}
							shortSummaryIcon={
								<ThumbUpIcon
									sx={{
										marginLeft: 1,
										rotate: feedbacksSummary.recommendationStatus.deg,
										color: 'rgba(0, 0, 0, 0.54)',
									}}
								/>
							}
							type="recommendations"
							data={feedbacksSummary.recommendations}
						/>
					)}
					<Box marginY={feedbacksSummary.recommendations.length > 0 ? 2 : 0} />
					{feedbacksSummary.ratings.length > 0 && (
						<SummaryContent
							headerTitle={averageRating}
							shortSummaryTitle={String(Number(feedbacksSummary.allRatingsAverage.toFixed(1)))}
							shortSummaryIcon={
								<Rating
									name="read-only"
									value={Number(feedbacksSummary.allRatingsAverage.toFixed(1))}
									readOnly
									precision={0.1}
									aria-hidden="true"
									sx={{ marginLeft: 1, color: 'rgb(250, 175, 0)' }}
								/>
							}
							type="averageRating"
							data={feedbacksSummary.ratings}
						/>
					)}
				</>
			)}
		</Box>
	);
};

export default Summary;
